import { ref, ssrRef } from '@nuxtjs/composition-api'
import { productService } from '@propeller-commerce/propeller-sdk-v2'
import type { Product } from '@propeller-commerce/propeller-sdk-v2'
import { getPropellerClient, propellerConfig } from './client'

// `getProduct` is a fixed generated document that does not request the
// crossupsell relations, and takes no parameter to add them. They drive
// three things at once: the Voordeelpakket banner on the detail page, the
// related products below it, and the suggestions in the add-to-cart modal — all
// three rendered empty (EXAMS-138 QA). Fetched here as a separate call and
// merged onto the product.
//
// `crossupsellsFrom`, not `crossupsellsTo`: the former holds the relations
// configured ON this product, with the recommended product in `productTo`. The
// latter holds relations other products point at it with, where `productTo` is
// this product itself — which is why the detail page recommended itself
// (EXAMS-138, Menno 25-08).
const CROSSUPSELLS_QUERY = `
  fragment CrossupsellProduct on Product {
    productId
    names { language value }
    shortNames { language value }
    slugs { language value }
    price { net gross }
    media { images { items { priority tags { language values } images { originalUrl } } } }
    attributes(input: $attributeResultSearchInput) {
      items {
        attributeDescription { name }
        value { ... on AttributeTextValue { textValues { values } } }
      }
    }
  }

  query ProductCrossupsells($productId: Int!, $language: String!, $attributeResultSearchInput: AttributeResultSearchInput) {
    product(productId: $productId, language: $language) {
      productId
      hidden
      categories { items { categoryId } }
      crossupsellsFrom {
        itemsFound
        items {
          type
          subType
          productTo { ...CrossupsellProduct }
          clusterTo { ...CrossupsellProduct }
        }
      }
    }
  }
`

interface CrossupsellsResult {
  crossupsellsFrom: unknown
  /** Category ids the product is filed under, or null when the call failed. */
  categoryIds: number[] | null
  /** Back-office entry that should never get a public page. */
  hidden: boolean
}

/** Never throws: the detail page renders without cross-sell if this fails. */
async function fetchCrossupsells (productId: number, language: string): Promise<CrossupsellsResult> {
  try {
    const data = await getPropellerClient().query(CROSSUPSELLS_QUERY, {
      productId,
      language,
      attributeResultSearchInput: { offset: 500, page: 1 }
    }, 'ProductCrossupsells') as {
      product?: {
        crossupsellsFrom?: unknown
        hidden?: string
        categories?: { items?: { categoryId?: number }[] }
      }
    }
    const categoryIds = (data?.product?.categories?.items ?? [])
      .map(item => item?.categoryId)
      .filter((id): id is number => typeof id === 'number')
    return {
      crossupsellsFrom: data?.product?.crossupsellsFrom ?? null,
      categoryIds,
      hidden: data?.product?.hidden === 'Y'
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Propeller crossupsells request failed:', err)
    return { crossupsellsFrom: null, categoryIds: null, hidden: false }
  }
}

/**
 * Product detail composable (v2).
 *
 * Same consumer surface as before: `const { products, search, loading } =
 * useProduct(cacheId)` with `search({ id })` fetching the product detail.
 * `products` holds a single product object (naming kept so call sites stay
 * drop-in).
 *
 * Now backed by the SDK `productService(client).getProduct(...)` instead of a
 * hand-rolled query. `getProduct` returns the full product (attributes, media,
 * price, inventory, bundles) so the page getters work unchanged.
 *
 * Errors do NOT throw: like before, a failed search leaves `products` as `{}`
 * so pages can render their 404 path (`pages/products/_id/_slug.vue`).
 */

interface ProductSearchParams {
  id: string | number
}

export function useProduct (cacheId?: string) {
  // `ssrRef`, not `ref`: the fetched product has to survive the SSR -> client
  // handover. With a plain ref the client started empty, so the page flashed its
  // skeleton and — because vue-meta evaluates `head()` before the client refetch
  // resolves and does not re-track a setup() ref — the product title and meta
  // description were replaced by the layout defaults right after hydration.
  // The v1 page did not have this because Vue Storefront kept the product in
  // shared SSR state. Keyed per product so navigating between products does not
  // reuse the previous one.
  const products = ssrRef<Product | Record<string, never>>({}, `useProduct-${cacheId ?? 'default'}`)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const search = async (params: ProductSearchParams): Promise<void> => {
    loading.value = true
    error.value = null
    try {
      const productId = parseInt(String(params.id), 10)
      const product = await productService(getPropellerClient()).getProduct({
        productId,
        language: propellerConfig.language,
        // Fetch the full attribute set. The default page returns only ~12
        // attributes (all TM_*), which truncates the EXAMEN_* attributes the
        // product-spec table renders → empty "Productspecificaties" (EXAMS-138
        // QA). `offset` acts as the page size on this endpoint.
        attributeResultSearchInput: { offset: 500, page: 1 }
      })
      if (!product) {
        products.value = {}
        return
      }
      const { crossupsellsFrom, categoryIds, hidden } = await fetchCrossupsells(productId, propellerConfig.language)
      // `getProduct` resolves ANY product id, including ones that were taken out
      // of the Examenbundel catalogue — a title moved to the Import Folder still
      // rendered a complete, orderable detail page (EXAMS-138, ean
      // 9789006992168). Treat "not filed under the catalogue root" as not found.
      // `categoryIds === null` means the lookup itself failed; stay open then
      // rather than 404-ing a valid product on a hiccup.
      if (hidden || (categoryIds && !categoryIds.includes(propellerConfig.catalogRootCategoryId))) {
        products.value = {}
        return
      }
      products.value = { ...product, crossupsellsFrom } as Product
    } catch (err) {
      error.value = err instanceof Error ? err.message : String(err)
      // eslint-disable-next-line no-console
      console.error('Propeller getProduct request failed:', err)
      products.value = {}
    } finally {
      loading.value = false
    }
  }

  return { products, search, loading, error }
}
